import { Box, Button, Chip, Stack, Typography, type ChipProps } from "@mui/material"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import type { PublicMessage } from "appsShared/inAppMessages"
import { actionTarget, messageLocale } from "./InAppMessageCard"

/** Spec R18: no tag field — the tag comes from `kind` and `severity`. */
export function feedTag(message: PublicMessage): {
  labelKey: string
  color: ChipProps["color"]
} {
  if (message.kind === "announcement")
    return { labelKey: "inAppFeedTagNew", color: "primary" }
  if (message.severity === "outage")
    return { labelKey: "inAppFeedTagOutage", color: "error" }
  if (message.severity === "warning")
    return { labelKey: "inAppFeedTagWarning", color: "warning" }
  return { labelKey: "inAppFeedTagInfo", color: "info" }
}

type Props = {
  message: PublicMessage
  unread: boolean
  /** Called after an in-app action navigates, so the drawer closes. */
  onNavigate: () => void
}

/** One feed item. Text renders as React text nodes only — never HTML (spec §Security). */
export const InAppFeedItem: FC<Props> = ({ message, unread, onNavigate }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = messageLocale(i18n.language ?? "fr")
  const target = message.action ? actionTarget(message.action.href) : null
  const tag = feedTag(message)
  const date = message.publishedAt
    ? new Date(message.publishedAt).toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null

  return (
    <Box
      component="li"
      data-message-id={message.id}
      data-unread={unread || undefined}
      sx={{ listStyle: "none", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip size="small" label={t(tag.labelKey)} color={tag.color} variant="outlined" />
        {date && (
          <Typography variant="caption" color="text.secondary">
            {date}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {unread && (
          <Box
            role="img"
            aria-label={t("inAppFeedUnread")}
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }}
          />
        )}
      </Stack>
      {message.title && (
        <Typography variant="subtitle2" fontWeight="bold">
          {message.title[locale]}
        </Typography>
      )}
      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
        {message.body[locale]}
      </Typography>
      {target?.kind === "external" && message.action && (
        <Button
          size="small"
          sx={{ mt: 1, px: 0 }}
          component="a"
          href={target.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {message.action.label[locale]}
        </Button>
      )}
      {target?.kind === "route" && message.action && (
        <Button
          size="small"
          sx={{ mt: 1, px: 0 }}
          onClick={() => {
            navigate(target.href)
            onNavigate()
          }}
        >
          {message.action.label[locale]}
        </Button>
      )}
    </Box>
  )
}
